import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Button from '@mui/material/Button';
import BackIcon from '@mui/icons-material/Reply';
import '../App.css';
import '../css/UsuariosAdmin.css';
import Input from './Input';
import Loading from './Loading';
import { request } from '../utils/ApiUtils';
import { showNotification } from '../utils/NotificationUtils';
import { rolesMapping } from '../utils/RolesUtils';
import { getUserId } from '../utils/TokenUtils';

const roleNames = ['contratos', 'clientes', 'colaboradores', 'comissao', 'admin', 'configdespesas', 'balancofinanceiro', 'despesas'];

const roleLabels = {
  contratos: 'Contratos', clientes: 'Clientes', colaboradores: 'Colaboradores', comissao: 'Comissão',
  admin: 'Admin', configdespesas: 'Config. despesas', balancofinanceiro: 'Balanço financeiro', despesas: 'Despesas'
};

function UsuarioAdminForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [originalEmail, setOriginalEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const currentUserId = getUserId();

  useEffect(() => {
    request('admin/usuarios', {
      type: 'GET',
      callback: (users) => {
        const selectedUser = (users || []).find((item) => item.id === id);
        if (selectedUser) {
          setUser(selectedUser);
          setOriginalEmail(selectedUser.email || '');
        } else {
          showNotification('Usuário não encontrado.', 'error');
          navigate('/admin/usuarios');
        }
      },
      callbackError: (_, message) => showNotification(message || 'Não foi possível carregar o usuário.', 'error'),
      finally: () => setLoading(false)
    });
  }, [id, navigate]);

  const isAssigned = (roleName) => rolesMapping[roleName].every((technicalRole) => user.roles?.includes(technicalRole));

  const toggleRole = (roleName, checked) => {
    const selectedRoles = roleNames.filter(isAssigned);
    const newSelectedRoles = checked ? [...new Set([...selectedRoles, roleName])] : selectedRoles.filter((name) => name !== roleName);
    setUser((current) => ({ ...current, roles: [...new Set(newSelectedRoles.flatMap((name) => rolesMapping[name]))] }));
  };

  const saveEmail = (updatedUser, email) => {
    request(`admin/usuarios/${updatedUser.id}/email`, {
      type: 'PUT',
      body: JSON.stringify({ email }),
      callback: (savedUser) => {
        setUser({ ...updatedUser, ...(savedUser || {}), email });
        setOriginalEmail(email);
        showNotification('Usuário salvo.', 'success');
      },
      callbackError: (_, message) => showNotification(message || 'Não foi possível atualizar o e-mail.', 'error'),
      finally: () => setSaving(false)
    });
  };

  const saveUser = () => {
    const email = user.email?.trim() || '';
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      showNotification('Informe um e-mail válido.', 'error');
      return;
    }

    const userToSave = { ...user, email };
    setSaving(true);
    request(`admin/usuarios/${id}/roles`, {
      type: 'PUT',
      body: JSON.stringify({ roles: userToSave.roles || [] }),
      callback: (updatedUser) => {
        const userWithRoles = { ...userToSave, ...updatedUser };
        if (email !== originalEmail) {
          saveEmail(userWithRoles, email);
        } else {
          setUser(userWithRoles);
          showNotification('Usuário salvo.', 'success');
          setSaving(false);
        }
      },
      callbackError: (_, message) => {
        showNotification(message || 'Não foi possível atualizar as permissões.', 'error');
        setSaving(false);
      }
    });
  };

  if (loading) return <Loading />;
  if (!user) return null;

  return (
    <div className="form-container">
      <div id="notification-container" className="notification-container usuarios-admin-notifications" />
      <div className="header-container-form">
        <h1 style={{ color: '#5E5E5E' }}>Usuário</h1>
        <Button variant="text" onClick={() => navigate('/admin/usuarios')} startIcon={<BackIcon style={{ fontSize: '2rem', fontWeight: 'bold', color: '#0057a3' }} />} />
      </div>

      <div className="row">
        <Input id="username" value={user.username} parameters={{ width: '30%', label: 'Usuário', disabled: true, onChange: () => {} }} />
        <Input id="email" value={user.email} parameters={{ width: '35%', type: 'email', label: 'E-mail*', onChange: (email) => setUser((current) => ({ ...current, email })) }} />
      </div>

      <div className="usuarios-admin-form-roles">
        <h3>Permissões</h3>
        <div className="usuarios-admin-roles">
          {roleNames.map((roleName) => (
            <label key={roleName}>
              <input
                type="checkbox"
                checked={isAssigned(roleName)}
                disabled={saving || (user.id === currentUserId && roleName === 'admin')}
                onChange={(event) => toggleRole(roleName, event.target.checked)}
              />
              {roleLabels[roleName]}
            </label>
          ))}
        </div>
      </div>

      <button type="button" onClick={saveUser} className="add-button" disabled={saving}>
        Salvar usuário
      </button>
    </div>
  );
}

export default UsuarioAdminForm;
